<template>
  <v-container class="fill-height" fluid>
    <v-row align="center" justify="center">
      <v-card
        class="teach-table-selector-card"
        :disabled="loading"
        :loading="loading"
      >
        <v-card-title> {{ $t(isGraduate ? "teach_table.title_x" : "teach_table.title") }} </v-card-title>
        <v-card-text>
          <v-tabs v-model="tab">
            <v-tabs-slider color="orange"></v-tabs-slider>
            <v-tab> {{ $t(isGraduate ? "teach_table.search_by_major" : "teach_table.search_by_curr_class") }} </v-tab>
            <v-tab> {{ $t("teach_table.search_by_course_id") }} </v-tab>
            <v-tab v-if="!isGraduate">
              {{ $t("teach_table.search_by_course_category") }}
            </v-tab>
          </v-tabs>
          <v-divider class="mt-3" />

          <v-tabs-items v-model="tab">
            <v-tab-item>
              <teach-table-selector-by-class
                :years="years"
                :semesters="semesters"
                :class_years="class_years"
                :faculties="faculties"
                :departments="departments"
                :curriculums="curriculums"
                :branches="branches"
                :admission_types="admission_types"
                :admission_semesters="admission_semesters"
                :config_permission="config_permission"
                :level_id="levelId"
              />
            </v-tab-item>
            <v-tab-item>
              <teach-table-selector-by-subject-id
                :years="years"
                :semesters="semesters"
                :class_years="class_years"
                :faculties="faculties"
                :departments="departments"
                :curriculums="curriculums"
                :branches="branches"
                :admission_types="admission_types"
                :admission_semesters="admission_semesters"
                :level_id="levelId"
              />
            </v-tab-item>
            <v-tab-item v-if="!isGraduate">
              <teach-table-selector-by-category
                :years="years"
                :semesters="semesters"
                :faculties="faculties"
                :subject_owners="subject_owners"
              />
            </v-tab-item>
          </v-tabs-items>
          <v-alert v-if="loadError" class="mt-4 mb-0" type="error" text>
            {{ $t("teach_table.selector_load_error") }}
          </v-alert>
        </v-card-text>
      </v-card>
    </v-row>
  </v-container>
</template>

<script>
import dayjs from "dayjs";
import teachTableProvider from "@/services/teach_table.provider.js";
import genericProvider from "@/services/generic.provider.js";
import refProvider from "@/services/ref.provider.js";
import TeachTableSelectorByClass from "./TeachTableSelectorByClass.vue";
import TeachTableSelectorBySubjectId from "./TeachTableSelectorBySubjectId.vue";
import TeachTableSelectorByCategory from "./TeachTableSelectorByCategory.vue";
const teachTableService = new teachTableProvider();
const genericService = new genericProvider();
const refService = new refProvider();

const semesters = ["1", "2", "3"].sort((a, b) => b - a);
const graduateSemesters = [1, 2];
const class_years = ["1", "2", "3", "4", "5", "6"].sort();

export default {
  props: {
    levelId: {
      type: Number,
      default: 1,
    },
  },
  components: {
    TeachTableSelectorByClass,
    TeachTableSelectorBySubjectId,
    TeachTableSelectorByCategory,
  },

  data: () => ({
    loading: false,
    loadError: false,
    years: [],
    semesters: semesters,
    class_years: class_years,
    faculties: [],
    departments: [],
    curriculums: [],
    branches: [],
    admission_types: [],
    admission_semesters: [],
    subject_owners: [],
    tab: null,
    config_permission: { academic_year: [], user_permission: [] },
  }),

  computed: {
    isGraduate() {
      return Number(this.levelId) > 1;
    },
  },

  methods: {
    async fetch_data() {
      this.loading = true;
      this.setAcademicYears();
      const facultiesPromise = genericService.getFaculty();
      const departmentsPromise = genericService.getAllRegistrarDepartment();

      if (this.isGraduate) {
        try {
          this.loadError = false;
          this.semesters = [...graduateSemesters];
          this.admission_semesters = [...graduateSemesters];

          // Central API รับ LEVEL_ID ได้ครั้งละค่า จึงรวมระดับ 2 และ 3
          // เพื่อให้ได้ผลเทียบเท่า LEVEL_ID IN (2, 3)
          const [
            faculties,
            departments,
            masterCurriculums,
            doctoralCurriculums,
            masterAdmissionTypes,
            doctoralAdmissionTypes,
            registrarBranches,
          ] = await Promise.all([
            facultiesPromise,
            departmentsPromise,
            genericService.getCurriculum({ LEVEL_ID: 2 }),
            genericService.getCurriculum({ LEVEL_ID: 3 }),
            refService.getStudyAdmitTypeByLevel(2),
            refService.getStudyAdmitTypeByLevel(3),
            genericService.getAllRegistrarBranch(),
          ]);
          const graduateCurriculums = this.normalizeGraduateCurriculums([
            ...(masterCurriculums || []),
            ...(doctoralCurriculums || []),
          ]);
          // คณะและภาควิชาต้องแสดงจาก master API ให้ครบก่อนเสมอ
          // ส่วนหลักสูตรและแขนงจึงค่อยกรองตามภาควิชาที่ผู้ใช้เลือก
          this.setBaseDimensions(faculties, departments);
          this.admission_types = this.normalizeGraduateAdmissionTypes([
            ...(masterAdmissionTypes || []),
            ...(doctoralAdmissionTypes || []),
          ]);
          this.curriculums = graduateCurriculums;
          this.branches = registrarBranches || [];
        } catch {
          this.loadError = true;
        } finally {
          this.loading = false;
        }

        // สิทธิ์แก้ไขไม่ใช่ข้อมูลตัวเลือก หาก token หมดอายุหรือ endpoint นี้
        // ตอบ error ให้คงสิทธิ์ว่างไว้ โดยไม่แสดงข้อความว่าฟิลเตอร์โหลดไม่ได้
        if (this.$store.state.user.is_officer === true) {
          try {
            this.config_permission =
              await teachTableService.getTeachTableConfigDateTime();
          } catch {
            this.config_permission = {
              academic_year: [],
              user_permission: [],
            };
          }
        }
        return;
      }

      const [faculties, departments, curriculums] = await Promise.all([
        facultiesPromise,
        departmentsPromise,
        genericService.getCurriculum({ LEVEL_ID: 1 }),
      ]);
      // Preserve all registrar departments, as in the original undergraduate flow.
      // Service departments may have no matching degree curriculum.
      this.setBaseDimensions(faculties, departments);
      this.curriculums = curriculums.sort(
        (a, b) => a.CURRICULUM_ID - b.CURRICULUM_ID
      );

      const subject_owners =
        await refService.getTeachTableSubjectOwnerOnlyTeachService();
      this.subject_owners = subject_owners;

      if (this.$store.state.user.is_officer === true) {
        this.config_permission =
          await teachTableService.getTeachTableConfigDateTime();
      }

      this.loading = false;
    },

    setAcademicYears() {
      const maxYear = parseInt(dayjs().format("BBBB"));
      const minYear = this.isGraduate ? 2565 : 2563;
      this.years = [];
      for (let year = minYear; year <= maxYear; year++) {
        this.years.push({
          year,
          year_th: String(year),
          year_en: String(year - 543),
        });
      }
      this.years.sort((a, b) => b.year - a.year);
    },
    normalizeGraduateCurriculums(curriculums) {
      const uniqueCurriculums = new Map();
      (curriculums || []).forEach((item) => {
        const registrarCurriculumId = String(
          item.REGISTRAR_CURRICULUM_ID || ""
        ).trim();
        if (
          !registrarCurriculumId ||
          registrarCurriculumId.toLowerCase() === "x"
        )
          return;
        const key = `${item.FACULTY_ID}|${item.REGISTRAR_DEPARTMENT_ID}|${registrarCurriculumId}`;
        const current = uniqueCurriculums.get(key);
        if (
          !current ||
          Number(item.YEAR || 0) > Number(current.YEAR || 0) ||
          (Number(item.YEAR || 0) === Number(current.YEAR || 0) &&
            Number(item.CURRICULUM_ID || 0) >
              Number(current.CURRICULUM_ID || 0))
        ) {
          uniqueCurriculums.set(key, item);
        }
      });
      return [...uniqueCurriculums.values()]
        .map((item) => {
          const shortNameTh = String(item.SHORT_NAME_TH || "").trim();
          return {
            ...item,
            SHORT_NAME_TH:
              Number(item.LEVEL_ID) === 3 &&
              !shortNameTh.includes("(ดุษฎีบัณฑิต)")
                ? `${shortNameTh} (ดุษฎีบัณฑิต)`
                : shortNameTh,
          };
        })
        .sort((a, b) =>
          String(a.SHORT_NAME_TH || "").localeCompare(
            String(b.SHORT_NAME_TH || ""),
            "th"
          )
        );
    },
    normalizeGraduateAdmissionTypes(admissionTypes) {
      const uniqueTypes = new Map();
      (admissionTypes || []).forEach((item) => {
        const value = String(item.REGISTRAR_ADMIT_TYPE_ID || "").trim();
        if (!value || uniqueTypes.has(value)) return;
        uniqueTypes.set(value, {
          value,
          text_th: item.STUDY_ADMIT_TYPE_NAME_TH || value,
          text_en:
            item.STUDY_ADMIT_TYPE_NAME_EN ||
            item.STUDY_ADMIT_TYPE_NAME_TH ||
            value,
        });
      });
      return [...uniqueTypes.values()].sort(
        (a, b) => Number(a.value) - Number(b.value)
      );
    },
    setBaseDimensions(faculties, departments) {
      this.faculties = [...(faculties || [])]
        .sort((a, b) => a.FACULTY_ID - b.FACULTY_ID)
        .map((item) => ({
          ...item,
          NAME_TH: `${item.PREFIX_NAME_TH || ""}${item.FACULTY_NAME_TH || ""}`,
          NAME_EN: `${item.PREFIX_NAME_EN || ""} ${
            item.FACULTY_NAME_EN || ""
          }`.trim(),
        }));
      this.departments = [...(departments || [])].sort(
        (a, b) => a.department_id - b.department_id
      );
    },
  },

  created() {
    this.fetch_data();
  },

  watch: {
    levelId(newValue, oldValue) {
      if (newValue === oldValue) return;
      this.loadError = false;
      this.years = [];
      this.semesters = this.isGraduate ? graduateSemesters : semesters;
      this.faculties = [];
      this.departments = [];
      this.curriculums = [];
      this.branches = [];
      this.admission_types = [];
      this.admission_semesters = [];
      this.subject_owners = [];
      this.fetch_data();
    },
  },
};
</script>

<style scoped>
.teach-table-selector-card {
  width: 800px;
  max-width: calc(100vw - 32px);
}
</style>
